import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { SxStyle } from '../../../../../types';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import SquadDialog, { SquadDialogProps } from '../squad-dialog';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import TeamPlayerList from '../team-player-list';
import { SquadPlayer } from '../../types';
import CircularProgress from '@mui/material/CircularProgress';
import { usePlayer } from '../../hooks/player';
import { useSquad } from '../../hooks/squad';
import { useTeam } from '../../hooks/team';
import { PlayerMinimized } from '../../api-types';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';

interface Props extends Pick<SquadDialogProps, 'open' | 'onClose'> {
  season: number;
  teamId: string;
  onLoading: (loading: boolean) => void;
  onSubmitted: () => void;
  selectedSquad: SquadPlayer[] | null;
}

const styles: SxStyle = {
  seasonField: {
    marginTop: '20px',
  },
  subTitle: {
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '16px',
  },
  contentList: {
    padding: '16px',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
  },
};

export default function AddPlayerModal({
  open,
  onClose,
  onLoading,
  onSubmitted,
  season,
  teamId,
  selectedSquad,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [teamPlayers, setTeamPlayers] = useState<PlayerMinimized[] | null>(null);
  const [addedPlayers, setAddedPlayers] = useState<PlayerMinimized[]>([]);
  const { assignPlayers } = usePlayer();
  const { fetchTeam } = useTeam();
  const { createSquad } = useSquad();

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      if (!selectedSquad) {
        let playerIds = '';
        addedPlayers.forEach((player, index) => {
          const comma = index + 1 < addedPlayers.length;
          playerIds += player.fogisId + (comma ? ',' : '');
        });
        await createSquad(teamId, season, playerIds);
      } else {
        await assignPlayers(teamId, season, addedPlayers);
      }
      onSubmitted();
      if (onClose) {
        onClose();
      }
    } finally {
      setSubmitLoading(false);
    }
  }, [
    addedPlayers,
    assignPlayers,
    createSquad,
    onClose,
    onSubmitted,
    season,
    teamId,
    selectedSquad,
  ]);

  useEffect(() => {
    const getPlayers = async () => {
      onLoading(true);
      try {
        const players = await fetchTeam(teamId, season);
        if (players) {
          setTeamPlayers(players);
        }
      } finally {
        setLoading(false);
        onLoading(false);
      }
    };
    getPlayers();
  }, [teamId, onLoading, enqueueSnackbar, strings, season, fetchTeam]);

  useEffect(() => {
    setSubmitDisabled(!(addedPlayers?.length > 0));
  }, [addedPlayers]);

  const onAddPlayer = (player: PlayerMinimized) => {
    setAddedPlayers((currentAddedPlayers) => {
      return [...currentAddedPlayers, player];
    });
  };

  const omRemovePlayer = (player: PlayerMinimized) => {
    const updatedSelectedPlayers = addedPlayers.filter(
      (addedPlayer) => addedPlayer.fogisId !== player.fogisId
    );
    setAddedPlayers(updatedSelectedPlayers);
  };

  if (loading) {
    return <></>;
  }

  return (
    <SquadDialog
      open={open}
      onClose={onClose}
      title={Array.isArray(selectedSquad) ? strings.squad.addPlayer : strings.squad.createSquad}
    >
      {loading ? (
        <DialogContent sx={styles.content}>
          <CircularProgress />
        </DialogContent>
      ) : teamPlayers && teamPlayers.length > 0 ? (
        <>
          <DialogContentText sx={styles.subTitle}>
            <Typography variant="italic">{strings.squad.fogisInfo}</Typography>
          </DialogContentText>
          <DialogContent sx={styles.contentList} dividers>
            <TeamPlayerList
              players={teamPlayers}
              addedPlayers={addedPlayers}
              onAddPlayer={onAddPlayer}
              onRemovePlayer={omRemovePlayer}
            />
          </DialogContent>
        </>
      ) : (
        <DialogContent sx={styles.content}>
          <Typography variant="italic">{strings.squad.teamPlayersEmpty}</Typography>
        </DialogContent>
      )}
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {strings.actions.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={submitDisabled}
          loading={submitLoading}
          onClick={onSubmit}
        >
          {strings.actions.save}
        </LoadingButton>
      </DialogActions>
    </SquadDialog>
  );
}
